import { Controller } from "@hotwired/stimulus"

// Drag-and-drop reordering for project cards using the HTML5 Drag and Drop API.
//
// Mount on the cards list container:
//   data-controller="card-drag-sort"
//   data-card-drag-sort-url-value="/projects/:id/cards/reorder"
//
// Each draggable item (turbo-frame wrapping a card) needs:
//   data-card-drag-sort-target="item"
//   data-card-id="<card.id>"
//   draggable="true"
//
// A drag handle element inside each card can optionally carry:
//   data-drag-handle  (clicking elsewhere still works but handle limits hit area)

export default class extends Controller {
  static targets = ["item"]
  static values  = { url: String }

  connect() {
    this._draggedItem = null
    this._placeholder = null
  }

  // ---- drag lifecycle ----

  dragstart(event) {
    const item = this._itemFromEvent(event)
    if (!item) return

    this._draggedItem = item
    item.classList.add("opacity-40", "cursor-grabbing")

    // Required for Firefox
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", item.dataset.cardId)
  }

  dragover(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const target = this._itemFromEvent(event)
    if (!target || target === this._draggedItem) return

    const rect = target.getBoundingClientRect()
    const midY = rect.top + rect.height / 2

    if (event.clientY < midY) {
      this.element.insertBefore(this._draggedItem, target)
    } else {
      this.element.insertBefore(this._draggedItem, target.nextSibling)
    }
  }

  dragend(event) {
    const item = this._draggedItem
    if (item) {
      item.classList.remove("opacity-40", "cursor-grabbing")
    }
    this._draggedItem = null
    this._save()
  }

  // ---- helpers ----

  _itemFromEvent(event) {
    // Walk up from the event target to find the nearest item target
    let el = event.target
    while (el && el !== this.element) {
      if (this.itemTargets.includes(el)) return el
      el = el.parentElement
    }
    return null
  }

  async _save() {
    const cardIds = this.itemTargets
      .map(el => el.dataset.cardId)
      .filter(Boolean)

    if (cardIds.length === 0) return

    const csrf = document.querySelector("meta[name='csrf-token']")?.content

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Accept":       "text/vnd.turbo-stream.html",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf
        },
        body: JSON.stringify({ card_ids: cardIds })
      })

      if (response.ok) {
        const html = await response.text()
        if (html.trim().length > 0) {
          Turbo.renderStreamMessage(html)
        }
      }
    } catch {
      // Silent fail -- order reverts on next page load
    }
  }
}
